import { t } from 'i18next';
import { isNil, isString } from 'lodash-es';
import { PRICING_CONFIGURATION_TYPE_LABEL_LOOKUP } from '~/common/translators';
import { TranslationKeys } from '~/common/types';
import { CHANGE_SET_TYPE, CHANGE_TYPE } from '~/connect/schemas';
import { IChangeLog, IFormattedChangeLogField } from '~/connect/types';
// eslint-disable-next-line @wyz/module/bad-module-import, @wyz/module/invalid-import
import { variantDiameterLabel } from '~/purchase/helpers/pricing-policies/distribution';
// eslint-disable-next-line @wyz/module/bad-module-import
import { PRICING_TYPE_ENUM } from '~/purchase/schemas';
// eslint-disable-next-line @wyz/module/bad-module-import
import { IDistributionExtraBonusVariant } from '~/purchase/types';

type IChangeSet = IChangeLog['changeSet'][number];

// Surfaced elsewhere (author column) or redundant → never shown in the document column.
const HIDDEN_PATHS = ['updatedAt', 'updatedBy'];
// Mongo bookkeeping keys that carry no business meaning once ids are unwrapped.
const DROP_KEYS = ['_id', '__v'];

const ROOT_GROUP_KEY = '__root__';

const isIndexSegment = (segment: string) => /^\d+$/.test(segment);

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Humanize a single path segment for display (step 1: no i18n mapping yet).
 * "RcsNumber" -> "Rcs Number", "DirectDelivery" -> "Direct Delivery", "ID" -> "ID".
 */
export function humanizeSegment(segment: string): string {
  const spaced = segment
    .replace(/^\$/, '') // Mongo operator marker ($oid, $ref…)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2') // camelCase boundary
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2') // ACRONYMWord boundary
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** Format a primitive for display, or null when there is nothing to show. */
export function formatScalar(value: unknown): string | null {
  if (isNil(value)) {
    return null;
  }
  if (isString(value)) {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

/** Author (updatedBy) is a plain string; kept simple for the dedicated column. */
export function formatChangeValue(value: unknown): string | null {
  return formatScalar(value);
}

/** Ids/hashes (long, pure alphanumeric) get monospaced — never emails, numbers or spaced text. */
export function isTokenValue(value: string): boolean {
  return value.length > 20 && /^[A-Za-z0-9]+$/.test(value);
}

// Field names that carry a timestamp (…AcceptedAt, createdDate, lockTime…).
const DATE_NAME_RE = /(?:at|date|time|timestamp)$/i;

/** A ms epoch in a plausible range (~1973–5138), i.e. not a small int or an id. */
export function isEpochMillis(value: string | null): boolean {
  if (value === null || !/^\d+$/.test(value)) {
    return false;
  }
  const n = Number(value);
  return n >= 1e11 && n < 1e14;
}

// BSON type markers the differ can leave inside a path (`…AcceptedAt.$numberLong`);
// they are noise in the label but the date name lives on a sibling segment.
const isMongoMarker = (segment: string) => segment.startsWith('$');

/** True when a field is a timestamp: a date-ish business segment AND an epoch-ms value. */
function isDateField(segments: string[], sample: string | null): boolean {
  return segments.some((s) => DATE_NAME_RE.test(s)) && isEpochMillis(sample);
}

/** A JSON string coming from the API (e.g. '{"$oid":"…"}') is parsed back to a value. */
function coerceValue(raw: unknown): unknown {
  if (isString(raw)) {
    const trimmed = raw.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return raw;
      }
    }
  }
  return raw;
}

/**
 * Unwrap MongoDB extended JSON so values read as business data, never as `{…}`:
 * `{$oid}` / `{$numberX}` / `{$date}` collapse to their primitive, DBRefs
 * (`{$ref,$id}`) collapse to the referenced id, and `_id`/`__v` are dropped.
 */
function normalizeMongo(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeMongo);
  }
  if (!isPlainObject(value)) {
    return value;
  }

  const keys = Object.keys(value);
  if (keys.length === 1) {
    if ('$oid' in value) return value.$oid;
    if ('$date' in value) return value.$date;
    if ('$numberInt' in value) return value.$numberInt;
    if ('$numberLong' in value) return value.$numberLong;
    if ('$numberDouble' in value) return value.$numberDouble;
    if ('$numberDecimal' in value) return value.$numberDecimal;
  }
  if ('$ref' in value && '$id' in value) {
    return normalizeMongo(value.$id);
  }

  const out: Record<string, unknown> = {};
  for (const key of keys) {
    if (DROP_KEYS.includes(key)) {
      continue;
    }
    out[key] = normalizeMongo(value[key]);
  }
  return out;
}

interface ILeaf {
  path: string[];
  scalar: string | null;
}

/** Flatten a normalized value into scalar leaves, one per business field. */
function flattenValue(value: unknown, prefix: string[] = []): ILeaf[] {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return [{ path: prefix, scalar: null }];
    }
    return value.flatMap((item, index) =>
      flattenValue(item, [...prefix, String(index + 1)]),
    );
  }
  if (isPlainObject(value)) {
    const entries = Object.entries(value);
    if (entries.length === 0) {
      return [{ path: prefix, scalar: null }];
    }
    return entries.flatMap(([key, item]) =>
      flattenValue(item, [...prefix, key]),
    );
  }
  return [{ path: prefix, scalar: formatScalar(value) }];
}

const leaves = (raw: unknown): ILeaf[] =>
  isNil(raw) ? [] : flattenValue(normalizeMongo(coerceValue(raw)));

interface IChangeGroup {
  key: string;
  label: string | null; // "Criteria #1" for array entities, null for top-level fields
  fields: IFormattedChangeLogField[];
  actionType: IChangeSet['type'] | null; // set when every field shares one action
}

// A field with neither an old nor a new value to show is noise (e.g. "— Added").
const isBlank = (value: string | null) => value === null || value === '';

/**
 * Turn one changeSet entry into one or more display fields: scalar changes stay a
 * single old→new row, object/DBRef values are unwrapped and flattened into a row
 * per leaf (`Value › Label`, `Value › Default`…) instead of a raw JSON dump.
 */
function toFormattedFields(
  change: IChangeSet,
  fieldSegments: string[],
): IFormattedChangeLogField[] {
  // Value resolution mirrors the legacy change-logs-table behaviour.
  let rawOld: unknown = null;
  let rawNew: unknown = null;
  if (change.type === CHANGE_SET_TYPE.DELETED) {
    rawOld = change.oldValue;
  } else if (!isNil(change.newValue)) {
    rawOld = change.oldValue;
    rawNew = change.newValue;
  } else {
    rawNew = change.value;
  }

  const order: string[] = [];
  const byKey = new Map<
    string,
    { path: string[]; oldValue: string | null; newValue: string | null }
  >();
  const collect = (list: ILeaf[], side: 'oldValue' | 'newValue') => {
    for (const leaf of list) {
      const key = leaf.path.join('.');
      let entry = byKey.get(key);
      if (!entry) {
        entry = { path: leaf.path, oldValue: null, newValue: null };
        byKey.set(key, entry);
        order.push(key);
      }
      entry[side] = leaf.scalar;
    }
  };
  collect(leaves(rawOld), 'oldValue');
  collect(leaves(rawNew), 'newValue');

  return order.map((key) => {
    const entry = byKey.get(key) as {
      path: string[];
      oldValue: string | null;
      newValue: string | null;
    };
    const rawSegments = [...fieldSegments, ...entry.path].filter(Boolean);
    // Drop BSON markers ($numberLong, $oid…) — they carry no business meaning.
    const displaySegments = rawSegments.filter((s) => !isMongoMarker(s));
    const labelSegments =
      displaySegments.length > 0
        ? displaySegments
        : [change.path.split('.').pop() ?? change.path];
    return {
      key: `${change.path}#${key}`,
      label: labelSegments.map(humanizeSegment).join(' › '),
      type: change.type,
      oldValue: entry.oldValue,
      newValue: entry.newValue,
      isDate: isDateField(labelSegments, entry.newValue ?? entry.oldValue),
    };
  });
}

/**
 * Group a change log's changeSet by array-entity prefix so that paths like
 * `Criteria.0.Value.Label` render under a "Criteria #1" header, while top-level
 * fields (`RcsNumber`, `DeliveryInformation.DirectDelivery`) stay ungrouped.
 * Insertion order is preserved.
 */
export function buildChangeGroups(log: IChangeLog): IChangeGroup[] {
  const groups: IChangeGroup[] = [];
  const byKey = new Map<string, IChangeGroup>();

  const relevant = log.changeSet.filter(
    (change) =>
      !HIDDEN_PATHS.includes(change.path) &&
      // A creation lists every field as ADDED; hiding those keeps it readable.
      !(
        change.type === CHANGE_SET_TYPE.ADDED && log.type === CHANGE_TYPE.INSERT
      ),
  );

  for (const change of relevant) {
    const segments = change.path.split('.');
    const indexPos = segments.findIndex(isIndexSegment);

    let groupKey = ROOT_GROUP_KEY;
    let groupLabel: string | null = null;
    let fieldSegments = segments;

    if (indexPos !== -1) {
      groupKey = segments.slice(0, indexPos + 1).join('.');
      const entity = segments
        .slice(0, indexPos)
        .map(humanizeSegment)
        .join(' › ');
      groupLabel = `${entity} #${Number(segments[indexPos]) + 1}`;
      fieldSegments = segments.slice(indexPos + 1);
    }

    let group = byKey.get(groupKey);
    if (!group) {
      group = {
        key: groupKey,
        label: groupLabel,
        fields: [],
        actionType: null,
      };
      byKey.set(groupKey, group);
      groups.push(group);
    }
    group.fields.push(...toFormattedFields(change, fieldSegments));
  }

  // Drop blank fields, then empty groups, and flag groups with a single action.
  return groups
    .map((group) => {
      const fields = group.fields.filter(
        (f) => !(isBlank(f.oldValue) && isBlank(f.newValue)),
      );
      const types = new Set(fields.map((f) => f.type));
      return {
        ...group,
        fields,
        actionType: types.size === 1 ? [...types][0] : null,
      };
    })
    .filter((group) => group.fields.length > 0);
}

type IValueModifiedLog = {
  path: 'values' | 'rules';
  type: IChangeSet['type'];
  brand: string;
  supplierTypeId: string;
  oldValue: IDistributionExtraBonusVariant;
  newValue: IDistributionExtraBonusVariant;
  value: IDistributionExtraBonusVariant;
};
function extractOpFromVariant(variant: IDistributionExtraBonusVariant) {
  return t('app.distribution.pricing_policy.detail.diameter', {
    diameter: variantDiameterLabel(variant),
  });
}

function extractExtraDataFromVariant(
  value: IDistributionExtraBonusVariant,
  seasonTranslations: { [id: string]: keyof TranslationKeys },
) {
  const supplierTag = t('app.user.groups.supplier');
  let label = `${supplierTag} #${value.supplierTypeId}`;
  label = label.concat(` > ${t(seasonTranslations[value.seasonId!])}`);
  if (value.vehicleType != null) {
    label = label.concat(` > ${value.vehicleType.name}`);
  }
  if (value.diameterOperator !== null) {
    label = label.concat(` > ${extractOpFromVariant(value)}`);
  }

  return label;
}

export function buildPOLChangeGroups(
  log: IChangeLog,
  seasonTranslations: { [id: number]: keyof TranslationKeys },
): IChangeGroup[] {
  const bonusTypeFields = ['hqBonusType', 'bonusType'];
  const simpleFields = ['hqBonus', ...bonusTypeFields];
  const groups: IChangeGroup[] = [];
  const relevant = log.changeSet.filter(
    (change) => !HIDDEN_PATHS.includes(change.path),
  ) as IValueModifiedLog[];
  const bonusTypeOptions: { [name: string]: string } = Object.values(
    PRICING_TYPE_ENUM,
  ).reduce(
    (acc, value) => ({
      ...acc,
      [value]: t(PRICING_CONFIGURATION_TYPE_LABEL_LOOKUP[value]),
    }),
    {},
  );

  const supplierTag = t('app.user.groups.supplier');
  const fields: IFormattedChangeLogField[] = [];
  for (const change of relevant) {
    const field: IFormattedChangeLogField = {
      key: change.path,
      isDate: false,
      label: `${supplierTag} #${change.supplierTypeId} > ${change.path}`,
      type: change.type,
      oldValue: null,
      newValue: null,
    };

    if (change.supplierTypeId === null) {
      field.label = `${change.path}`;
    }

    if (simpleFields.includes(change.path)) {
      const oldValue = change.oldValue as unknown as string;
      const newValue = change.newValue as unknown as string;
      const value = change.value as unknown as string;
      field.oldValue = change.type !== CHANGE_SET_TYPE.ADDED ? oldValue : null;
      field.newValue = change.type !== CHANGE_SET_TYPE.ADDED ? newValue : value;
      if (bonusTypeFields.includes(change.path)) {
        field.oldValue =
          change.type !== CHANGE_SET_TYPE.ADDED
            ? bonusTypeOptions[oldValue]
            : null;
        field.newValue =
          change.type !== CHANGE_SET_TYPE.ADDED
            ? bonusTypeOptions[newValue]
            : bonusTypeOptions[value];
      }
    } else if (change.path === 'values') {
      if (change.type === CHANGE_SET_TYPE.ADDED) {
        field.label = extractExtraDataFromVariant(
          change.value,
          seasonTranslations,
        );
        field.newValue = `${change.value.value}`;
      } else if (change.type === CHANGE_SET_TYPE.MODIFIED) {
        field.label = extractExtraDataFromVariant(
          change.newValue,
          seasonTranslations,
        );
        field.oldValue = `${change.oldValue.value}`;
        field.newValue = `${change.newValue.value}`;
      }
    } else if (change.path === 'rules') {
      const { value } = change;
      field.label = `${supplierTag} #${value.supplierTypeId} > ${extractOpFromVariant(value)}`;
      if (change.type === CHANGE_SET_TYPE.ADDED) {
        field.newValue = `${value.value}`;
      }
    }
    fields.push(field);
  }
  groups.push({
    key: log.changeSet[0].brand!,
    label: log.changeSet[0].brand!,
    actionType: null,
    fields,
  });
  return groups;
}

/** Extract the author (updatedBy) surfaced in the dedicated column. */
export function extractUpdatedBy(log: IChangeLog): string | null {
  const entry = log.changeSet.find((change) => change.path === 'updatedBy');
  if (!entry) {
    return null;
  }
  return formatScalar(!isNil(entry.newValue) ? entry.newValue : entry.value);
}
